import { createRef } from 'react';
import {IContentBlock, IDesignBlock} from '../../types/iblock';
import { IImageBlockData } from '../../types/content-block-data';
import { escapeHTML } from '../../lib/page-builder-lib';
import pageBuilderStore from '../../stores/pageBuilderStore';
import blocksManager from '../../stores/blocks-manager';
import contentManager from '../../stores/content-manager';

/**
 * Converts an ImageBlock (IContentBlock) to an HTML string.
 * Renders as a <figure> with an <img> and optional <figcaption>.
 *
 * @param block - The IContentBlock (should be blockType "image")
 * @returns HTML string (e.g., <figure><img src="..." ... /><figcaption>...</figcaption></figure>)
 * 
 * @remarks
 * - Added a new local variable `isEmail` for toggling serialization markup.
 */
export function fromImageBlockToHTML(block: IContentBlock): string {
  if (block.blockType !== "image") return "";

  const isEmail = pageBuilderStore.getIsEmail();
  const data = block.blockData as IImageBlockData;

  const src = escapeHTML(data.src ?? "");
  const alt = escapeHTML(data.alt ?? data.caption ?? "");
  const description = escapeHTML(data.description ?? "");
  const caption = data.caption ? `<figcaption>${escapeHTML(data.caption)}</figcaption>` : "";

  let imageStyle = `width: ${data.width?.value ? `${data.width.value}${data.width.unit || "%"}` : "100%"}; `;
  imageStyle += `height: ${data.height?.value && data.height?.value !== "" ? `${data.height.value}${data.height.unit || "px"}` : "auto" };`;
  imageStyle += isEmail && `margin: ${blocksManager.dimensionToCss(data.margin) || "0px"};`;
  imageStyle += `display: block;`;
  imageStyle += `object-fit: ${data.imageFit || "cover"};`;
  imageStyle += `object-position: ${data.imagePosition || "center"};`;
  imageStyle += `border-radius: ${blocksManager.dimensionToCss(data.borderRadius) || "8px"};`;

  let imgHtml =  `<figure class="max-w-full border border-dashed border-gray-300"
      style="display: flex; flex-direction: column; align-items:${
        data.alignment === "center"
          ? "center"
          : data.alignment === "right"
            ? "flex-end"
            : "flex-start"};
      padding: ${blocksManager.dimensionToCss(data.margin)};"><img src="${src}" alt="${alt}" aria-description="${description}" class="${block.selector}" style="${imageStyle}" /><span style="text-align: ${data.alignment};">${caption}</span></figure>`;

  return isEmail 
    ? `<tr><td styles="padding: ${blocksManager.dimensionToCss(data.margin) || "0px"};">${imgHtml}</td></tr>` 
    : imgHtml;    
}

/**
 * Serializes an ImageBlock's data for storage or export.
 * Omits UI-only fields.
 *
 * @param block - The IContentBlock (should be blockType "image")
 * @returns A JSON-serializable object
 */
export function fromImageBlockToJSON(block: IContentBlock): object {
  if (block.blockType !== "image") return {};
  const data = block.blockData as IImageBlockData;
  return {
    contentBlockId: block.contentBlockId,
    blockType: block.blockType,
    selector: block.selector,
    blockData: {
      src: data.src ?? "",
      caption: data.caption ?? "",
      description: data.description ?? "",
    },
  };
}

/**
 * Reconstructs an ImageBlock from JSON data.
 *
 * @param json - The JSON object to parse
 * @returns An IContentBlock instance (for use in your store/UI)
 */
export function fromJSONToImageBlock(json: any, context: "canvas" | "columns" = "canvas"): IDesignBlock {
  const imageStyles = contentManager.getStylesForSelector(json.selector);
    
  const newDesignBlockRef = createRef<HTMLDivElement>();
  const newContentBlockRef = createRef<HTMLElement>(); // generic for now

  const block: IDesignBlock = {
    designBlockId: crypto.randomUUID(), // always generate fresh
    designBlockRef: newDesignBlockRef,
    contentBlock: {
      contentBlockId: json.contentBlockId,
      blockType: json.blockType,
      contentBlockRef: newContentBlockRef,
      selector: json.selector,
      blockData: {
        src: json.blockData?.src ?? "",
        caption: json.blockData?.caption ?? "",
        description: json.blockData?.description ?? "",
        margin: blocksManager.parseDimensions(imageStyles?.["margin"], "four-side"),
        borderRadius: blocksManager.parseDimensions(imageStyles?.["border-radius"], "four-corners"),
        width: blocksManager.parseDimensions(imageStyles?.["width"], "single"),
        height: imageStyles?.["height"] === "auto" ? "auto" : blocksManager.parseDimensions(imageStyles?.["height"], "single"),
        imageFit: json.blockData?.imageFit ?? "cover",
        imagePosition: json.blockData?.imagePosition ?? "center",
        alignment: inferAlignmentFromMargins(imageStyles) ?? "left"
      } as IImageBlockData,
    } as IContentBlock,
  };

  blocksManager.addDesignTreeEntry({
		contentBlockId: block.contentBlock.contentBlockId,
		designBlockId: block.designBlockId,
		designBlockRef: newDesignBlockRef,
		contentBlockRef: newContentBlockRef,
		contentBlockType: block.contentBlock.blockType.toUpperCase(),
		location: context,
	});

  return block;
}

function inferAlignmentFromMargins(styles: Record<string, string> | undefined): "left" | "center" | "right" {
  if (!styles) return "left";
  const ml = styles["margin-left"] ?? styles["marginLeft"];
  const mr = styles["margin-right"] ?? styles["marginRight"];

  if (ml === "auto" && mr === "auto") return "center";
  if (ml === "auto" && (mr === "0" || mr === "0px")) return "right";
  return "left";
}